import { useMemo } from 'react';

import { fromPromise } from 'xstate5';

import {
  type FetchInitialDataParams,
  type FetchInitialDataResponse,
} from '@order/features-ordering';
import { useFetchFlexPaymentMethod } from '@order/features-payments';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { getTips } from '../../utils';
import { useBagLoyaltyData } from '../useBagLoyaltyData';
import { useBagPaymentInfo } from '../useBagPaymentInfo';
import { useCart } from '../useCart';
import { useCredits } from '../useCredits';
import { useDefaultPaymentMethod } from '../useDefaultPaymentMethod';
import { useFetchTelemetryData } from '../useFetchTelemetryData';
import { useGiftCards } from '../useGiftCards';
import { usePaymentMethods } from '../usePaymentMethods';

export const useFetchInitialData = () => {
  const isLoggedIn = useIsLoggedIn();
  const dynamicTips = useFeatureFlag('CELS-1191-permanent-dynamic-tip-values');

  const { fetchCart } = useCart();
  const { fetchCredits } = useCredits();
  const { fetchLoyaltyData } = useBagLoyaltyData();
  const { fetchGiftCardBalance } = useGiftCards(fetchCredits);
  const { fetchBagPaymentInfo } = useBagPaymentInfo();
  const { fetchPaymentMethods } = usePaymentMethods();
  const { fetchDefaultPaymentMethod } = useDefaultPaymentMethod();
  const { fetchFlexPaymentMethod } = useFetchFlexPaymentMethod();
  const { fetchTelemetryData } = useFetchTelemetryData();

  const fetchInitialData = useMemo(
    () =>
      fromPromise<FetchInitialDataResponse, FetchInitialDataParams>(async () => {
        if (!isLoggedIn) {
          const cart = await fetchCart();

          return {
            cart,
            paymentMethods: [],
            defaultPaymentMethodId: undefined,
            flexPaymentMethod: undefined,
            defaultTip: 0,
            availablePoints: 0,
            availableCredit: 0,
            giftCardBalance: 0,
            selectedTip: 0,
            availableTips: [],
            customerId: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            stripeCustomerId: '',
            stripeCustomerKey: '',
            stripeSession: '',
            isNewCustomer: true,
            isOptedOutFromLoyalty: false,
            hasDefaultPaymentMethodCard: false,
          };
        }

        const [
          cart,
          paymentMethods,
          defaultPaymentMethodId,
          flexPaymentMethod,
          availableCredit,
          giftCardBalance,
          telemetryData,
          loyaltyData,
          bagPaymentInfo,
        ] = await Promise.all([
          fetchCart(),
          fetchPaymentMethods(),
          fetchDefaultPaymentMethod(),
          fetchFlexPaymentMethod(),
          fetchCredits(),
          fetchGiftCardBalance(),
          fetchTelemetryData(),
          fetchLoyaltyData(),
          fetchBagPaymentInfo(),
        ]);

        const tips = getTips({
          maxTipAmount: cart.maxTipAmount,
          orderChannel: cart.orderChannel,
          dynamicTips,
        });

        const hasDefaultPaymentMethodCard = paymentMethods.some(
          (pm) => pm.id === defaultPaymentMethodId,
        );

        return {
          cart,
          paymentMethods,
          defaultPaymentMethodId,
          flexPaymentMethod,
          availableCredit,
          giftCardBalance,
          hasDefaultPaymentMethodCard,
          ...tips,
          ...bagPaymentInfo,
          ...loyaltyData,
          ...telemetryData,
        };
      }),
    [
      isLoggedIn,
      dynamicTips,
      fetchCart,
      fetchPaymentMethods,
      fetchDefaultPaymentMethod,
      fetchFlexPaymentMethod,
      fetchCredits,
      fetchGiftCardBalance,
      fetchTelemetryData,
      fetchLoyaltyData,
      fetchBagPaymentInfo,
    ],
  );

  return { fetchInitialData };
};
